import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { Input } from "@ember/component";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { cancel, next, schedule } from "@ember/runloop";
import { service } from "@ember/service";
import { modifier } from "ember-modifier";
import EmojiPicker from "discourse/components/emoji-picker";
import PluginOutlet from "discourse/components/plugin-outlet";
import lazyHash from "discourse/helpers/lazy-hash";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import getURL from "discourse/lib/get-url";
import discourseLater from "discourse/lib/later";
import { applyValueTransformer } from "discourse/lib/transformer";
import { updateUserStatusOnMention } from "discourse/lib/update-user-status-on-mention";
import isZoomed from "discourse/lib/zoom-check";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import ChatMessageAvatar from "discourse/plugins/chat/discourse/components/chat/message/avatar";
import ChatMessageError from "discourse/plugins/chat/discourse/components/chat/message/error";
import ChatMessageInfo from "discourse/plugins/chat/discourse/components/chat/message/info";
import ChatMessageLeftGutter from "discourse/plugins/chat/discourse/components/chat/message/left-gutter";
import ChatMessageBlocks from "discourse/plugins/chat/discourse/components/chat-message/blocks";
import ChatMessageActionsDesktop from "discourse/plugins/chat/discourse/components/chat-message-actions-desktop";
import ChatMessageActionsMobileModal from "discourse/plugins/chat/discourse/components/chat-message-actions-mobile";
import ChatMessageInReplyToIndicator from "discourse/plugins/chat/discourse/components/chat-message-in-reply-to-indicator";
import ChatMessageReaction from "discourse/plugins/chat/discourse/components/chat-message-reaction";
import ChatMessageSeparator from "discourse/plugins/chat/discourse/components/chat-message-separator";
import ChatMessageText from "discourse/plugins/chat/discourse/components/chat-message-text";
import ChatMessageThreadIndicator from "discourse/plugins/chat/discourse/components/chat-message-thread-indicator";
import ChatMessageInteractor from "discourse/plugins/chat/discourse/lib/chat-message-interactor";
import ChatOnLongPress from "discourse/plugins/chat/discourse/modifiers/chat/on-long-press";

let _chatMessageDecorators = [];

export function addChatMessageDecorator(decorator) {
  _chatMessageDecorators.push(decorator);
}

export function resetChatMessageDecorators() {
  _chatMessageDecorators = [];
}

export const MENTION_KEYWORDS = ["here", "all"];
export const MESSAGE_CONTEXT_THREAD = "thread";

export default class ChatMessage extends Component {
  @service site;
  @service siteSettings;
  @service currentUser;
  @service chat;
  @service chatApi;
  @service chatChannelPane;
  @service chatThreadPane;
  @service modal;
  @service interactedChatMessage;

  @tracked isActive = false;

  toggleCheckIfPossible = modifier((element) => {
    let addedListener = false;

    const handler = (event) => {
      if (!this.pane.selectingMessages) {
        return;
      }

      if (event.shiftKey) {
        this.messageInteractor.bulkSelect(!this.args.message.selected);
        return;
      }

      this.messageInteractor.select(!this.args.message.selected);
    };

    if (this.pane.selectingMessages) {
      element.addEventListener("click", handler, { passive: true });
      addedListener = true;
    }

    return () => {
      if (addedListener) {
        element.removeEventListener("click", handler);
      }
    };
  });

  get pane() {
    return this.threadContext ? this.chatThreadPane : this.chatChannelPane;
  }

  get hasConfirmationBlock() {
    return this.args.message.blocks?.some(
      (block) => block.type === "confirmation"
    );
  }

  get includeSeparator() {
    return this.args.includeSeparator ?? true;
  }

  get messageInteractor() {
    return new ChatMessageInteractor(
      getOwner(this),
      this.args.message,
      this.args.context
    );
  }

  get deletedAndCollapsed() {
    return this.args.message?.deletedAt && this.collapsed;
  }

  get hiddenAndCollapsed() {
    return this.args.message?.hidden && this.collapsed;
  }

  get collapsed() {
    return !this.args.message?.expanded;
  }

  get deletedMessageLabel() {
    let count = 1;

    const recursiveCount = (message) => {
      const previousMessage = message.previousMessage;
      if (previousMessage?.deletedAt) {
        count++;
        recursiveCount(previousMessage);
      }
    };

    recursiveCount(this.args.message);

    return i18n("chat.deleted", { count });
  }

  get shouldRender() {
    return (
      this.args.message.expanded ||
      !this.args.message.deletedAt ||
      (this.args.message.deletedAt && !this.args.message.nextMessage?.deletedAt)
    );
  }

  get shouldRenderReactions() {
    return this.siteSettings.enable_emoji && this.args.message.reactions.length;
  }

  get shouldRenderOpenEmojiPickerButton() {
    return (
      this.args.interactive !== false &&
      this.chat.userCanInteractWithChat &&
      this.site.desktopView
    );
  }

  get showActions() {
    return (
      this.chat.activeMessage?.model?.id === this.args.message.id &&
      this.chat.activeMessage?.context === this.args.context
    );
  }

  get secondaryActionsIsExpanded() {
    return document.querySelector(
      ".more-buttons.secondary-actions.is-expanded"
    );
  }

  get show() {
    return (
      !this.args.message?.deletedAt ||
      this.currentUser?.id === this.args.message?.user?.id ||
      this.currentUser?.staff ||
      this.args.message?.channel?.canModerate
    );
  }

  get isByCurrentUser() {
    return this.currentUser?.id === this.args.message?.user?.id;
  }

  get hasActiveState() {
    return (
      this.isActive ||
      (this.chat.activeMessage?.model?.id === this.args.message.id &&
        this.chat.activeMessage?.context === this.args.context)
    );
  }

  get hasReply() {
    return this.args.message.inReplyTo && !this.hideReplyToInfo;
  }

  get hideUserInfo() {
    const message = this.args.message;

    if (message.isAction) {
      return true;
    }

    if (message.pinned) {
      return false;
    }

    const previousMessage = message.previousMessage;

    if (!previousMessage) {
      return false;
    }

    // this is a micro optimization to avoid layout changes when we load more messages
    if (message.firstOfResults) {
      return false;
    }

    if (message.chatWebhookEvent) {
      return false;
    }

    if (previousMessage.deletedAt) {
      return false;
    }

    if (previousMessage.isAction) {
      return false;
    }

    if (
      Math.abs(
        new Date(message.createdAt) - new Date(previousMessage.createdAt)
      ) > 300000
    ) {
      return false;
    }

    if (message.inReplyTo) {
      if (message.inReplyTo?.id === previousMessage.id) {
        return message.user?.id === previousMessage.user?.id;
      } else {
        return false;
      }
    }

    return message.user?.id === previousMessage.user?.id;
  }

  get hideReplyToInfo() {
    return (
      this.threadContext ||
      this.args.message?.inReplyTo?.id ===
        this.args.message?.previousMessage?.id ||
      this.threadingEnabled
    );
  }

  get threadingEnabled() {
    return (
      (this.args.message?.channel?.threadingEnabled ||
        this.args.message?.thread?.force) &&
      !!this.args.message?.thread
    );
  }

  get showThreadIndicator() {
    return (
      !this.threadContext &&
      this.threadingEnabled &&
      this.args.message?.thread &&
      this.args.message?.thread.preview.replyCount > 0
    );
  }

  get threadContext() {
    return this.args.context === MESSAGE_CONTEXT_THREAD;
  }

  get shouldRenderStopMessageStreamingButton() {
    return (
      this.args.message.streaming &&
      (this.currentUser?.admin ||
        this.args.message.inReplyTo?.user?.id === this.currentUser?.id)
    );
  }

  @action
  expand() {
    const recursiveExpand = (message) => {
      const previousMessage = message.previousMessage;
      if (previousMessage?.deletedAt) {
        previousMessage.expanded = true;
        recursiveExpand(previousMessage);
      }
    };

    this.args.message.expanded = true;
    this.refreshStatusOnMentions();
    recursiveExpand(this.args.message);
  }

  @action
  toggleChecked(event) {
    event.stopPropagation();

    if (event.shiftKey) {
      this.messageInteractor.bulkSelect(event.target.checked);
      return;
    }

    this.messageInteractor.select(event.target.checked);
  }

  @action
  willDestroyMessage() {
    cancel(this._invitationSentTimer);
    cancel(this._disableMessageActionsHandler);
    cancel(this._makeMessageActiveHandler);
    cancel(this._onMouseEnterMessageDebouncedHandler);
    cancel(this._clearActiveMessageHandler);
    this.#teardownMentionedUsers();
    this.chat.activeMessage = null;
  }

  @action
  refreshStatusOnMentions() {
    schedule("afterRender", () => {
      this.args.message.mentionedUsers.forEach((user) => {
        const href = `/u/${user.username.toLowerCase()}`;
        const mentions = this.messageContainer.querySelectorAll(
          `a.mention[href="${href}"]`
        );

        mentions.forEach((mention) => {
          updateUserStatusOnMention(getOwner(this), mention, user.status);
        });
      });
    });
  }

  initMentionedUsers() {
    this.args.message.mentionedUsers.forEach((user) => {
      if (!user.statusManager.isTrackingStatus()) {
        user.statusManager.trackStatus();
        user.on("status-changed", this, "refreshStatusOnMentions");
      }
    });
  }

  decorateMentions(cooked) {
    if (this.args.message.channel.allowChannelWideMentions) {
      const wideMentions = [...cooked.querySelectorAll("span.mention")];
      MENTION_KEYWORDS.forEach((keyword) => {
        const mentions = wideMentions.filter((node) => {
          return node.textContent.trim() === `@${keyword}`;
        });

        const classes = applyValueTransformer("mentions-class", [], {
          user: { username: keyword },
        });

        mentions.forEach((mention) => {
          mention.classList.add(...classes);
        });
      });
    }

    this.args.message.mentionedUsers.forEach((user) => {
      const href = getURL(`/u/${user.username.toLowerCase()}`);
      const mentions = cooked.querySelectorAll(`a.mention[href="${href}"]`);
      const classes = applyValueTransformer("mentions-class", [], {
        user,
      });

      mentions.forEach((mention) => {
        mention.classList.add(...classes);
        updateUserStatusOnMention(getOwner(this), mention, user.status);
      });
    });
  }

  @bind
  decorateCookedMessage(element, helper) {
    this.messageContainer = element.closest(".chat-confirmation") || element;
    this.initMentionedUsers();
    this.decorateMentions(element);
    _chatMessageDecorators.forEach((decorator) => {
      decorator(element, helper, this.args.message);
    });
  }

  @action
  onMouseEnter() {
    if (this.site.mobileView) {
      return;
    }

    if (
      this.chat.activeMessage?.model?.id === this.args.message.id &&
      this.chat.activeMessage?.context === this.args.context
    ) {
      return;
    }

    if (this.interactedChatMessage.emojiPickerOpen) {
      return;
    }

    if (!this.secondaryActionsIsExpanded) {
      this._onMouseEnterMessageDebouncedHandler = discourseDebounce(
        this,
        this._debouncedOnHoverMessage,
        250
      );
    }
  }

  @action
  onMouseMove() {
    if (this.site.mobileView) {
      return;
    }

    if (
      this.chat.activeMessage?.model?.id === this.args.message.id &&
      this.chat.activeMessage?.context === this.args.context
    ) {
      return;
    }

    if (this.secondaryActionsIsExpanded) {
      return;
    }

    if (this.interactedChatMessage.emojiPickerOpen) {
      return;
    }

    this._setActiveMessage();
  }

  @action
  onFocusIn(event) {
    if (this.site.mobileView) {
      return;
    }

    const container = event.currentTarget;

    next(() => {
      if (container.isConnected && container.contains(document.activeElement)) {
        this._setActiveMessage({ fromKeyboard: true });
      }
    });
  }

  @action
  onFocusOut(event) {
    if (this.site.mobileView) {
      return;
    }

    const container = event.currentTarget;

    // The actions render inside the message, so focus moving into them stays contained.
    if (container.contains(event.relatedTarget)) {
      return;
    }

    // Deferred for the same reason as raising them, and rechecked: `relatedTarget` is not
    // always where focus comes to rest.
    this._clearActiveMessageHandler = next(() => {
      // The emoji picker and the secondary actions menu both take focus into a portal
      // outside the message, and both are anchored to the toolbar this would unmount.
      if (
        this.interactedChatMessage.emojiPickerOpen ||
        this.secondaryActionsIsExpanded
      ) {
        return;
      }

      if (
        container.isConnected &&
        !container.contains(document.activeElement) &&
        this.chat.activeMessage?.model?.id === this.args.message.id &&
        this.chat.activeMessage?.context === this.args.context
      ) {
        this.chat.activeMessage = null;
      }
    });
  }

  @action
  onMouseLeave(event) {
    cancel(this._onMouseEnterMessageDebouncedHandler);

    if (this.site.mobileView) {
      return;
    }

    if (
      (event.toElement || event.relatedTarget)?.closest(
        ".chat-message-actions-container"
      )
    ) {
      return;
    }

    if (this.interactedChatMessage.emojiPickerOpen) {
      return;
    }

    if (this.secondaryActionsIsExpanded) {
      return;
    }

    this.chat.activeMessage = null;
  }

  @action
  onLongPressStart(element, event) {
    if (!this.args.message.expanded || !this.args.message.persisted) {
      return;
    }

    if (event.target.tagName === "IMG") {
      return;
    }

    // prevents message to show as active when starting scroll
    // at this moment scroll has no momentum and the row can
    // capture the touch event instead of a scroll
    this._makeMessageActiveHandler = discourseLater(() => {
      this.isActive = true;
    }, 125);
  }

  @action
  onLongPressCancel() {
    cancel(this._makeMessageActiveHandler);
    this.isActive = false;

    // this a tricky bit of code which is needed to prevent the long press
    // from triggering a click on the message actions panel when releasing finger press
    // we can't prevent default as we need to keep the event passive for performance reasons
    // this class will prevent any click from being triggered until removed
    // this number has been chosen from testing but might need to be increased
    this._disableMessageActionsHandler = discourseLater(() => {
      document.documentElement.classList.remove(
        "disable-message-actions-touch"
      );
    }, 200);
  }

  @action
  onLongPressEnd(element, event) {
    if (this.args.interactive === false) {
      return;
    }

    if (event.target.tagName === "IMG") {
      return;
    }

    cancel(this._makeMessageActiveHandler);
    this.isActive = false;

    if (isZoomed()) {
      // if zoomed don't handle long press
      return;
    }

    document.documentElement.classList.add("disable-message-actions-touch");
    document.activeElement.blur();
    document.querySelector(".chat-composer__input")?.blur();

    this._setActiveMessage();
    this.modal.show(ChatMessageActionsMobileModal);
  }

  @action
  onEmojiPickerClose() {
    this.interactedChatMessage.emojiPickerOpen = false;
  }

  @action
  onEmojiPickerShow() {
    this.interactedChatMessage.emojiPickerOpen = true;
  }

  @action
  stopMessageStreaming(message) {
    this.chatApi.stopMessageStreaming(message.channel.id, message.id);
  }

  #teardownMentionedUsers() {
    this.args.message.mentionedUsers.forEach((user) => {
      user.statusManager.stopTrackingStatus();
      user.off("status-changed", this, "refreshStatusOnMentions");
    });
  }

  @bind
  _debouncedOnHoverMessage() {
    this._setActiveMessage();
  }

  // `disableMouseEvents` suppresses the toolbar while the list is being scrolled with the
  // pointer. Focus is not a pointer, so the keyboard path opts out of that check.
  _setActiveMessage({ fromKeyboard = false } = {}) {
    // A pending clear from focus leaving would otherwise land after this and undo it.
    cancel(this._clearActiveMessageHandler);

    if (
      (!fromKeyboard && this.args.disableMouseEvents) ||
      this.args.interactive === false
    ) {
      return;
    }

    cancel(this._onMouseEnterMessageDebouncedHandler);

    if (!this.args.message.expanded) {
      return;
    }

    // Focus moving between the message's own controls re-reports it as active. Assigning
    // a fresh hash each time invalidates what render just read from it.
    if (
      this.chat.activeMessage?.model?.id === this.args.message.id &&
      this.chat.activeMessage?.context === this.args.context
    ) {
      return;
    }

    this.chat.activeMessage = {
      model: this.args.message,
      hideUserInfo: this.hideUserInfo,
      context: this.args.context,
    };
  }

  <template>
    {{#if this.shouldRender}}
      {{#if this.includeSeparator}}
        <ChatMessageSeparator
          @fetchMessagesByDate={{@fetchMessagesByDate}}
          @message={{@message}}
        />
      {{/if}}

      {{! The message is not itself a control: these observe pointer and focus moving over
      it to raise its actions, and the controls they raise are the interactive parts. }}
      {{! eslint-disable-next-line ember/template-no-invalid-interactive }}
      <div
        class={{dConcatClass
          "chat-message-container"
          (if this.pane.selectingMessages "-selectable")
          (if @message.highlighted "-highlighted")
          (if @message.streaming "-streaming")
          (if (lt @message.user.id 0) "is-bot")
          (if this.isByCurrentUser "is-by-current-user")
          (if
            (eq
              @message.id
              @message.channel.currentUserMembership.lastReadMessageId
            )
            "-last-read"
          )
          (if @message.staged "-staged" "-persisted")
          (if @message.processed "-processed" "-not-processed")
          (if this.hasActiveState "-active")
          (if @message.bookmark "-bookmarked")
          (if @message.deletedAt "-deleted")
          (if @message.selected "-selected")
          (if @message.error "-errored")
          (if (eq @interactive false) "-not-interactive")
          (if this.showThreadIndicator "has-thread-indicator")
          (if this.hideUserInfo "-user-info-hidden")
          (if this.hasReply "has-reply")
        }}
        data-id={{@message.id}}
        data-thread-id={{@message.thread.id}}
        ...attributes
        {{willDestroy this.willDestroyMessage}}
        {{on "focusin" this.onFocusIn passive=true}}
        {{on "focusout" this.onFocusOut passive=true}}
        {{on "mouseenter" this.onMouseEnter passive=true}}
        {{on "mouseleave" this.onMouseLeave passive=true}}
        {{on "mousemove" this.onMouseMove passive=true}}
        {{this.toggleCheckIfPossible}}
        {{! the long-press actions modal only has a mobile layout }}
        {{ChatOnLongPress
          this.onLongPressStart
          this.onLongPressEnd
          this.onLongPressCancel
          enabled=this.site.mobileView
        }}
      >
        {{yield to="top"}}

        {{#if this.show}}
          {{#if this.pane.selectingMessages}}
            <Input
              class="chat-message-selector"
              @checked={{@message.selected}}
              @type="checkbox"
              {{on "click" this.toggleChecked}}
            />
          {{/if}}

          {{#if this.deletedAndCollapsed}}
            <div class="chat-message-text -deleted">
              <DButton
                class="btn-flat chat-message-expand"
                @action={{this.expand}}
                @translatedLabel={{this.deletedMessageLabel}}
              />
            </div>
          {{else if this.hiddenAndCollapsed}}
            <div class="chat-message-text -hidden">
              <DButton
                class="btn-flat chat-message-expand"
                @action={{this.expand}}
                @label="chat.hidden"
              />
            </div>
          {{else}}
            <div class="chat-message">
              {{#unless this.hideReplyToInfo}}
                <ChatMessageInReplyToIndicator @message={{@message}} />
              {{/unless}}

              {{#if this.hideUserInfo}}
                <ChatMessageLeftGutter
                  @message={{@message}}
                  @threadContext={{this.threadContext}}
                />
              {{else}}
                <ChatMessageAvatar
                  @interactive={{@interactive}}
                  @message={{@message}}
                />
              {{/if}}

              <div class="chat-message-content">
                <ChatMessageInfo
                  @context={{@context}}
                  @dateMode={{@dateMode}}
                  @interactive={{@interactive}}
                  @message={{@message}}
                  @show={{not this.hideUserInfo}}
                  @threadContext={{this.threadContext}}
                />

                {{#unless this.hasConfirmationBlock}}
                  <ChatMessageText
                    @cooked={{@message.cooked}}
                    @decorate={{this.decorateCookedMessage}}
                    @edited={{@message.edited}}
                    @uploads={{@message.uploads}}
                  />
                {{/unless}}

                {{#if this.shouldRenderStopMessageStreamingButton}}
                  <div class="stop-streaming-btn-container">
                    <DButton
                      class="stop-streaming-btn"
                      @action={{fn this.stopMessageStreaming @message}}
                      @icon="circle-stop"
                      @label="cancel"
                    />

                  </div>
                {{/if}}

                <ChatMessageBlocks
                  @decorate={{this.decorateCookedMessage}}
                  @message={{@message}}
                />

                <PluginOutlet
                  @name="chat-message-after"
                  @outletArgs={{lazyHash message=@message}}
                />

                {{#if this.shouldRenderReactions}}
                  <div class="chat-message-reaction-list">
                    {{#each @message.reactions as |reaction|}}
                      <ChatMessageReaction
                        @interactive={{@interactive}}
                        @message={{@message}}
                        @onReaction={{this.messageInteractor.react}}
                        @reaction={{reaction}}
                        @showTooltip={{true}}
                      />
                    {{/each}}

                    {{#if this.shouldRenderOpenEmojiPickerButton}}
                      <EmojiPicker
                        class="chat-message-reaction"
                        @btnClass="btn-flat react-btn chat-message-react-btn"
                        @context="chat"
                        @didSelectEmoji={{this.messageInteractor.selectReaction}}
                        @onClose={{this.onEmojiPickerClose}}
                        @onShow={{this.onEmojiPickerShow}}
                      />
                    {{/if}}
                  </div>
                {{/if}}

                <ChatMessageError
                  @message={{@message}}
                  @onRetry={{@resendStagedMessage}}
                />
              </div>

              {{#if this.showThreadIndicator}}
                <ChatMessageThreadIndicator @message={{@message}} />
              {{/if}}
            </div>
          {{/if}}
        {{/if}}

        {{#if this.showActions}}
          <ChatMessageActionsDesktop
            @context={{@context}}
            @message={{@message}}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
